import { useEffect, useState } from "react";
import {
  Drawer,
  DrawerContent,
  DrawerHeader,
  DrawerTitle,
  DrawerDescription,
} from "@/components/ui/drawer";
import { Badge } from "@/components/ui/badge";
import { ScrollArea } from "@/components/ui/scroll-area";
import { CheckCircle2, Clock, AlertCircle, FileText, Circle, Copy } from "lucide-react";
import { useToast } from "@/hooks/use-toast";
import { supabase } from "@/integrations/supabase/client";

interface ProcessingProgressDrawerProps {
  isOpen: boolean;
  onClose: () => void;
  documentId: string;
  documentName: string;
}

interface ProcessingStage {
  id: string;
  name: string;
  description: string;
}

const PROCESSING_STAGES: ProcessingStage[] = [
  { id: 'queued', name: 'Queued', description: 'Job accepted and queued for processing' },
  { id: 'downloading', name: 'Downloading', description: 'Fetching file from storage' },
  { id: 'ocr_prepare', name: 'Image Prep', description: 'Converting PDF to high-quality page images (DPI 250)' },
  { id: 'vision_extract', name: 'AI Vision', description: 'Extracting data with GPT-4 Vision (table structure recognition)' },
  { id: 'postprocess', name: 'Deduplication', description: 'Merging and deduplicating items across pages' },
  { id: 'saving', name: 'Saving Items', description: 'Writing extracted line items to database' },
  { id: 'completed', name: 'Completed', description: 'Image-first processing finished successfully' },
  { id: 'needs_review', name: 'Needs Review', description: 'No items detected - manual review required' },
  // Legacy stages for text fallback
  { id: 'pdf_to_png', name: 'PDF Conversion', description: 'Converting PDF to image for vision fallback' },
  { id: 'ai_extract', name: 'AI Processing', description: 'Extracting data with ChatGPT (text mode)' },
  { id: 'normalizing', name: 'Data Processing', description: 'Processing pricing and unit information' }
];

export function ProcessingProgressDrawer({ 
  isOpen, 
  onClose, 
  documentId, 
  documentName 
}: ProcessingProgressDrawerProps) {
  const [document, setDocument] = useState<any>(null);
  const [items, setItems] = useState<any[]>([]);
  const [events, setEvents] = useState<any[]>([]);
  const [openedAt] = useState(() => Date.now());
  const { toast } = useToast();

  useEffect(() => {
    if (!isOpen || !documentId) return;

    const fetchDocumentStatus = async () => {
      const { data, error } = await supabase
        .from('invoice_documents')
        .select('*')
        .eq('id', documentId)
        .single();

      if (!error && data) {
        setDocument(data);
      }
    };

    const fetchItems = async () => {
      const { data, error } = await supabase
        .from('invoice_items')
        .select('*')
        .eq('invoice_document_id', documentId)
        .order('created_at', { ascending: false });

      if (!error && data) {
        setItems(data);
      }
    };

    const fetchEvents = async () => {
      const { data, error } = await supabase
        .from('invoice_processing_events')
        .select('*')
        .eq('document_id', documentId)
        .order('ts', { ascending: false })
        .limit(20);

      if (!error && data) {
        setEvents(data);
      }
    };

    fetchDocumentStatus();
    fetchItems();
    fetchEvents();

    // Subscribe to real-time updates
    const channel = supabase
      .channel(`doc-progress-${documentId}`)
      .on('postgres_changes', {
        event: '*',
        schema: 'public',
        table: 'invoice_documents',
        filter: `id=eq.${documentId}`
      }, (payload) => {
        setDocument(payload.new);
      })
      .on('postgres_changes', {
        event: 'INSERT',
        schema: 'public',
        table: 'invoice_items',
        filter: `invoice_document_id=eq.${documentId}`
      }, () => {
        fetchItems();
      })
      .on('postgres_changes', {
        event: 'INSERT',
        schema: 'public',
        table: 'invoice_processing_events',
        filter: `document_id=eq.${documentId}`
      }, () => {
        fetchEvents();
      })
      .subscribe();

    return () => {
      supabase.removeChannel(channel);
    };
  }, [isOpen, documentId]);

  // Auto-close when processing completes successfully, but keep open for zero-item results
  useEffect(() => {
    if (document && document.processing_status === 'completed' && document.items_count > 0) {
      const timer = setTimeout(() => {
        onClose();
      }, 3000); // Auto-close after 3 seconds only if items were found
      
      return () => clearTimeout(timer);
    }
    // Keep drawer open if items_count is 0 (needs review) or if there's an error
  }, [document?.processing_status, document?.items_count, onClose]);

  const getStageStatus = (stageId: string) => {
    if (!document) return 'pending';
    
    if (document.processing_status === 'error') {
      return 'error';
    }
    
    if (document.stage === 'busy') {
      return stageId === 'queued' ? 'active' : 'pending';
    }
    
    if (document.stage === 'rate_limited' || document.stage === 'backoff_wait') {
      return stageId === 'ai_extract' ? 'active' : 'pending';
    }
    
    if (document.stage === stageId) {
      return document.processing_status === 'completed' ? 'completed' : 'active';
    }
    
    const currentStageIndex = PROCESSING_STAGES.findIndex(s => s.id === document.stage);
    const stageIndex = PROCESSING_STAGES.findIndex(s => s.id === stageId);
    
    if (stageIndex < currentStageIndex) return 'completed';
    if (stageIndex === currentStageIndex) return 'active';
    return 'pending';
  };

  // Calculate progress percentage and ETA
  const STAGES = ['queued','downloading','pdf_to_png','ai_extract','normalizing','saving','completed'];
  const currentStageIndex = Math.max(0, STAGES.indexOf(document?.stage || 'queued'));
  const isWaiting = document?.stage === 'rate_limited' || document?.stage === 'backoff_wait';
  const progressPercent = document?.stage === 'busy' ? 5 : 
                         isWaiting ? Math.round((STAGES.indexOf('ai_extract') / (STAGES.length - 1)) * 100) :
                         Math.round((currentStageIndex / (STAGES.length - 1)) * 100);
  
  const elapsedMs = Date.now() - openedAt;
  const eta = progressPercent > 0 && progressPercent < 100
    ? Math.round(elapsedMs * (100 - progressPercent) / progressPercent)
    : null;
  
  const formatTime = (ms: number) => `${Math.max(1, Math.round(ms/1000))}s`;

  const getStageIcon = (status: string) => {
    switch (status) {
      case 'completed':
        return <CheckCircle2 className="h-5 w-5 text-green-600" />;
      case 'active':
        return <Clock className="h-5 w-5 text-blue-600 animate-pulse" />;
      case 'error':
        return <AlertCircle className="h-5 w-5 text-red-600" />;
      default:
        return <Circle className="h-5 w-5 text-muted-foreground" />;
    }
  };

  // Enhanced action handlers for image-first processing
  const handleRetryAsVision = async () => {
    try {
      await supabase
        .from('invoice_documents')
        .update({
          processing_status: 'uploaded',
          processing_mode: 'image_first',
          force_ocr: true,
          stage: null,
          stage_message: 'Manual vision retry triggered',
          processing_lock: null,
          retry_count: 0,
          next_retry_at: null,
          items_count: 0
        })
        .eq('id', documentId);
      
      toast({
        title: "Vision Retry Triggered",
        description: "Document will be reprocessed with enhanced image analysis",
      });
    } catch (error) {
      console.error('Failed to trigger vision retry:', error);
      toast({
        title: "Error",
        description: "Failed to trigger vision retry",
        variant: "destructive",
      });
    }
  };

  const handleRetryAsText = async () => {
    try {
      await supabase
        .from('invoice_documents')
        .update({
          processing_status: 'uploaded',
          processing_mode: 'text_only',
          force_ocr: false,
          stage: null,
          stage_message: 'Manual text retry triggered',
          processing_lock: null,
          retry_count: 0,
          next_retry_at: null,
          items_count: 0
        })
        .eq('id', documentId);
      
      toast({
        title: "Text Retry Triggered",
        description: "Document will be reprocessed using text extraction",
      });
    } catch (error) {
      console.error('Failed to trigger text retry:', error);
      toast({
        title: "Error",
        description: "Failed to trigger text retry",
        variant: "destructive",
      });
    }
  };

  const handleRetryNow = async () => {
    try {
      await supabase
        .from('invoice_documents')
        .update({
          processing_status: 'uploaded',
          stage: null,
          stage_message: 'Manual retry triggered',
          processing_lock: null,
          retry_count: 0,
          next_retry_at: null,
          items_count: 0
        })
        .eq('id', documentId);
      
      toast({
        title: "Retry Triggered",
        description: "Document will be reprocessed shortly",
      });
    } catch (error) {
      console.error('Failed to trigger retry:', error);
      toast({
        title: "Error",
        description: "Failed to trigger retry",
        variant: "destructive",
      });
    }
  };

  const handleMarkFailed = async () => {
    try {
      await supabase
        .from('invoice_documents')
        .update({
          processing_status: 'error',
          stage: 'error',
          stage_message: 'Manually marked as failed',
          processing_lock: null,
          next_retry_at: null
        })
        .eq('id', documentId);
      
      toast({
        title: "Marked as Failed",
        description: "Document processing stopped",
      });
    } catch (error) {
      console.error('Failed to mark as failed:', error);
      toast({
        title: "Error",
        description: "Failed to mark as failed",
        variant: "destructive",
      });
    }
  };

  const handleClearLock = async () => {
    try {
      await supabase
        .from('invoice_documents')
        .update({
          processing_lock: null,
          stage_message: 'Processing lock cleared manually'
        })
        .eq('id', documentId);
      
      toast({
        title: "Lock Cleared",
        description: "Processing lock has been removed",
      });
    } catch (error) {
      console.error('Failed to clear lock:', error);
      toast({
        title: "Error",
        description: "Failed to clear lock",
        variant: "destructive",
      });
    }
  };

  const copyToClipboard = (text: string) => {
    navigator.clipboard.writeText(text);
    toast({
      title: "Copied",
      description: "Correlation ID copied to clipboard",
      duration: 2000,
    });
  };

  return (
    <Drawer open={isOpen} onOpenChange={onClose}>
      <DrawerContent className="h-[55vh] md:h-[80vh] max-h-[85svh]">
        <DrawerHeader>
          <DrawerTitle className="flex items-center gap-2">
            <FileText className="h-5 w-5" />
            Processing Progress
          </DrawerTitle>
          <DrawerDescription asChild>
            <div className="flex flex-col sm:flex-row sm:items-center gap-2">
              <span className="truncate max-w-[70vw] md:max-w-none">{documentName}</span>
              {document?.correlation_id && (
                <div className="flex items-center gap-2">
                  <code className="text-xs text-muted-foreground bg-muted px-2 py-1 rounded">
                    ID: {document.correlation_id.slice(-8)}
                  </code>
                  <button
                    onClick={() => copyToClipboard(document.correlation_id)}
                    className="p-1 hover:bg-muted rounded min-h-[44px] min-w-[44px] md:min-h-auto md:min-w-auto"
                    title="Copy full correlation ID"
                  >
                    <Copy className="h-3 w-3" />
                  </button>
                </div>
              )}
            </div>
          </DrawerDescription>
        </DrawerHeader>
        
        <ScrollArea className="flex-1 p-6 overflow-y-auto overflow-x-auto">
          <div className="space-y-6">
            {/* Progress Bar */}
            <div className="w-full space-y-2">
              <div className="flex items-center justify-between text-sm">
                <span className="font-medium">Progress</span>
                <span className="text-muted-foreground">
                  {progressPercent}% {eta ? `• ~${formatTime(eta)} remaining` : ''}
                </span>
              </div>
              <div className="h-2 w-full rounded bg-muted overflow-hidden">
                <div 
                  className="h-full bg-primary transition-all duration-500 ease-out" 
                  style={{ width: `${progressPercent}%` }} 
                />
              </div>
            </div>
            {/* Processing Timeline */}
            <div className="space-y-4">
              <h3 className="font-semibold text-sm text-muted-foreground uppercase tracking-wide">
                Processing Timeline
              </h3>
              
              <div className="space-y-3 md:space-y-2">
                {PROCESSING_STAGES.map((stage, index) => {
                  const status = getStageStatus(stage.id);
                  const isVisible = status !== 'pending' || index === 0;
                  
                  if (!isVisible && stage.id === 'pdf_to_png') {
                    // Only show PDF conversion if it's actually being used
                    return null;
                  }
                  
                  return (
                    <div key={stage.id} className="flex items-start gap-3">
                      <div className="flex-shrink-0 mt-0.5">
                        {getStageIcon(status)}
                      </div>
                      <div className="flex-1 min-w-0">
                        <div className="flex items-center gap-2">
                          <p className={`font-medium ${
                            status === 'completed' ? 'text-green-700' :
                            status === 'active' ? 'text-blue-700' :
                            status === 'error' ? 'text-red-700' :
                            'text-muted-foreground'
                          }`}>
                            {stage.name}
                          </p>
                          {status === 'active' && (
                            <Badge variant="outline" className="text-xs">
                              In Progress
                            </Badge>
                          )}
                        </div>
                        <p className="text-sm text-muted-foreground">
                          {stage.description}
                        </p>
                        {status === 'active' && document?.stage_message && (
                          <p className="text-xs text-blue-600 mt-1">
                            {document.stage_message}
                          </p>
                        )}
                      </div>
                    </div>
                  );
                })}
              </div>
            </div>

            {/* Backoff/Retry Status */}
            {document?.stage === 'backoff_wait' && document?.next_retry_at && (
              <div className="space-y-4">
                <h3 className="font-semibold text-sm text-orange-600 uppercase tracking-wide">
                  Waiting to Retry
                </h3>
                <div className="bg-orange-50 dark:bg-orange-900/20 border border-orange-200 dark:border-orange-800 rounded-lg p-4 space-y-3">
                  <p className="text-sm text-orange-800 dark:text-orange-200">
                    {document.stage_message || 'Waiting due to rate limiting or temporary failure'}
                  </p>
                  <div className="flex items-center gap-2 text-xs text-orange-700 dark:text-orange-300">
                    <span>Next retry:</span>
                    <code className="bg-orange-100 dark:bg-orange-800 px-2 py-1 rounded">
                      {new Date(document.next_retry_at).toLocaleTimeString()}
                    </code>
                    {document.retry_count > 0 && (
                      <span>• Attempt {document.retry_count}</span>
                    )}
                  </div>
                </div>
              </div>
            )}

            {/* Heartbeat and Lock Status */}
            {document && (
              <div className="space-y-4">
                <h3 className="font-semibold text-sm text-muted-foreground uppercase tracking-wide">
                  Processing Status
                </h3>
                <div className="bg-muted/30 rounded-lg p-4 space-y-2 text-sm">
                  {document.last_heartbeat_at && (
                    <div className="flex justify-between">
                      <span className="text-muted-foreground">Last heartbeat:</span>
                      <span>{new Date(document.last_heartbeat_at).toLocaleTimeString()}</span>
                    </div>
                  )}
                  {document.retry_count > 0 && (
                    <div className="flex justify-between">
                      <span className="text-muted-foreground">Retry count:</span>
                      <span>{document.retry_count}</span>
                    </div>
                  )}
                  {document.processing_lock && (
                    <div className="flex justify-between">
                      <span className="text-muted-foreground">Processing lock:</span>
                      <span className="font-mono text-xs">{document.processing_lock.slice(-8)}</span>
                    </div>
                  )}
                </div>
              </div>
            )}

            {/* Zero Items Detected - Reprocessing Options */}
            {document && (document.processing_status === 'needs_review' || (document.processing_status === 'completed' && document.items_count === 0)) && (
              <div className="space-y-4">
                <h3 className="font-semibold text-sm text-orange-600 uppercase tracking-wide">
                  No Items Detected
                </h3>
                <div className="bg-orange-50 dark:bg-orange-900/20 border border-orange-200 dark:border-orange-800 rounded-lg p-4 space-y-3">
                  <p className="text-sm text-orange-800 dark:text-orange-200">
                    No invoice items were extracted from this document. This could be due to:
                  </p>
                  <ul className="text-sm text-orange-700 dark:text-orange-300 list-disc list-inside space-y-1">
                    <li>Poor image quality or scanning artifacts</li>
                    <li>Complex table layouts that need text extraction</li>
                    <li>Non-standard invoice format</li>
                  </ul>
                  <div className="flex flex-wrap gap-2 pt-2">
                    <button
                      onClick={handleRetryAsText}
                      className="px-3 py-2 bg-blue-600 text-white text-sm rounded hover:bg-blue-700 transition-colors"
                    >
                      Reprocess as Text
                    </button>
                    <button
                      onClick={handleRetryAsVision}
                      className="px-3 py-2 bg-purple-600 text-white text-sm rounded hover:bg-purple-700 transition-colors"
                    >
                      Reprocess (Enhanced Vision)
                    </button>
                  </div>
                </div>
              </div>
            )}

            {/* Admin Actions */}
            {document && (document.processing_status === 'error' || document.stage === 'backoff_wait' || document.processing_lock) && (
              <div className="space-y-4">
                <h3 className="font-semibold text-sm text-muted-foreground uppercase tracking-wide">
                  Admin Actions
                </h3>
                <div className="flex flex-wrap gap-2">
                  <button
                    onClick={handleRetryNow}
                    className="px-3 py-2 bg-blue-600 text-white text-sm rounded hover:bg-blue-700 transition-colors"
                  >
                    Retry Now
                  </button>
                  <button
                    onClick={handleMarkFailed}
                    className="px-3 py-2 bg-red-600 text-white text-sm rounded hover:bg-red-700 transition-colors"
                  >
                    Mark Failed
                  </button>
                  {document.processing_lock && (
                    <button
                      onClick={handleClearLock}
                      className="px-3 py-2 bg-orange-600 text-white text-sm rounded hover:bg-orange-700 transition-colors"
                    >
                      Clear Lock
                    </button>
                  )}
                </div>
              </div>
            )}

            {/* Error Details */}
            {document?.processing_status === 'error' && (
              <div className="space-y-4">
                <h3 className="font-semibold text-sm text-red-600 uppercase tracking-wide">
                  Error Details
                </h3>
                <div className="bg-red-50 dark:bg-red-900/20 border border-red-200 dark:border-red-800 rounded-lg p-4 space-y-3">
                  <p className="text-sm text-red-800 dark:text-red-200">
                    {document.error_message || document.stage_message || 'An unknown error occurred'}
                  </p>
                  {document.correlation_id && (
                    <div className="flex items-center gap-2 pt-2 border-t border-red-200 dark:border-red-700">
                      <code className="text-xs bg-red-100 dark:bg-red-800 px-2 py-1 rounded">
                        {document.correlation_id}
                      </code>
                      <button
                        onClick={() => copyToClipboard(document.correlation_id)}
                        className="p-1 hover:bg-red-200 dark:hover:bg-red-700 rounded text-xs"
                        title="Copy correlation ID"
                      >
                        Copy ID
                      </button>
                    </div>
                  )}
                </div>
              </div>
            )}

            {/* Recent Events */}
            {events.length > 0 && (
              <div className="space-y-4">
                <h3 className="font-semibold text-sm text-muted-foreground uppercase tracking-wide">
                  Recent Events ({events.length})
                </h3>
                <div className="space-y-2 max-h-48 overflow-y-auto">
                  {events.slice(0, 10).map((event) => (
                    <div key={event.id} className="bg-muted/30 rounded p-3 text-sm">
                      <div className="flex items-center gap-2 mb-1">
                        <Badge variant={event.level === 'error' ? 'destructive' : event.level === 'warn' ? 'secondary' : 'outline'} className="text-xs">
                          {event.level}
                        </Badge>
                        <span className="text-muted-foreground text-xs">
                          {new Date(event.ts).toLocaleTimeString()}
                        </span>
                        {event.stage && (
                          <span className="text-muted-foreground text-xs">• {event.stage}</span>
                        )}
                      </div>
                      <p className="text-foreground">{event.message}</p>
                    </div>
                  ))}
                </div>
              </div>
            )}

            {/* Extracted Items Summary */}
            {items.length > 0 && (
              <div className="space-y-4">
                <h3 className="font-semibold text-sm text-muted-foreground uppercase tracking-wide">
                  Extracted Items ({items.length})
                </h3>
                <div className="grid gap-2">
                  {items.slice(0, 5).map((item, index) => (
                    <div key={item.id} className="bg-muted/50 rounded-lg p-3">
                      <p className="font-medium text-sm">{item.product_name}</p>
                      <div className="flex gap-4 text-xs text-muted-foreground mt-1">
                        {item.barcode_upc && <span>UPC: {item.barcode_upc}</span>}
                        {item.total_cost && <span>Cost: ${item.total_cost}</span>}
                        {item.quantity_boxes && <span>Qty: {item.quantity_boxes}</span>}
                      </div>
                    </div>
                  ))}
                  {items.length > 5 && (
                    <p className="text-xs text-muted-foreground text-center py-2">
                      ... and {items.length - 5} more items
                    </p>
                  )}
                </div>
              </div>
            )}
          </div>
        </ScrollArea>
      </DrawerContent>
    </Drawer>
  );
}